import { useEffect, useRef } from 'react';
import { useKeepAliveActive } from '@/common/hooks/useKeepAliveShown';

export const SEARCH_AUTO_RETRY_DELAY_MS = 3000;
// In step with the 503's Retry-After (120 s): at most one automatic reload per URL in this window.
export const SEARCH_AUTO_RETRY_TTL_MS = 120_000;

const keyOf = () => `search-auto-retry:${window.location.pathname}${window.location.search}`;

/** True when this URL may be reloaded automatically now. Without a working sessionStorage: false (fail-safe). */
const mayAutoReload = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(keyOf()));
    return !(last > 0 && Date.now() - last < SEARCH_AUTO_RETRY_TTL_MS);
  } catch {
    return false;
  }
};

/** Writes the stamp before the reload; false when it could not be stored (then no reload: it would loop). */
const markAutoReload = (): boolean => {
  try {
    window.sessionStorage.setItem(keyOf(), String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

/**
 * Automatic retry of a failed search page, a few seconds after it showed the error state.
 * - A full reload (not router.replace): a client navigation gets no SSR, so it would skip the
 *   server's degraded/503 check and could show the empty list again.
 * - A reload makes a new document, so a ref cannot remember it: the guard is a timestamp per URL in
 *   sessionStorage, written before the reload. During an outage that gives one reload per TTL, not a
 *   reload every few seconds. No usable storage => no automatic reload; the Retry button remains.
 * - Only while the page is active: a kept-alive search page stays mounted when the user leaves it,
 *   so the timer is cleared on hide (and starts again on Back, as a remount would).
 */
export const useSearchAutoRetry = (enabled: boolean, reload: () => void = () => window.location.reload()) => {
  const active = useKeepAliveActive();
  const done = useRef(false);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!enabled || !active || done.current || !mayAutoReload()) return;
    const timer = setTimeout(() => {
      if (!markAutoReload()) return;
      done.current = true;
      reloadRef.current();
    }, SEARCH_AUTO_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, active]);
};
